(async () => {

  if (!customElements.get('instagram-feed')) {

    const { default: Swiper, A11y, Autoplay, EffectFade, Pagination, Manipulation } = await import("swiper");
    
    Swiper.use([
      A11y,
      Autoplay,
      EffectFade,
      Pagination,
      Manipulation
    ]);

    class InstagramFeed extends HTMLElement {
      constructor() {
        super();

        this.feedUrl = this.dataset.feedUrl;
        this.numberOfPosts = parseInt(this.dataset.numberOfPosts);

        this.swiper = this.querySelector('.swiper');

        // Default to improve user experience
        this.swiperOptions = {
          threshold: 10,
          loop: false,
          roundLengths: true,
          slidesPerView: 1.25,
          spaceBetween: 16,
          slidesOffsetAfter: 16,
          watchSlidesProgress: true,
          breakpoints: {
            480: {
              slidesPerView: 2.25,
              spaceBetween: 16,
              slidesOffsetAfter: 16
            },
            768: {
              slidesPerView: 3.25,
              spaceBetween: 16,
              slidesOffsetAfter: 16
            },
            1024: {
              slidesPerView: 3.65,
              spaceBetween: 24,
              slidesOffsetAfter: 24
            },
            1440: {
              slidesPerView: 4.65,
              spaceBetween: 24,
              slidesOffsetAfter: 24
            },
            1920: {
              slidesPerView: 4.8,
              spaceBetween: 24,
              slidesOffsetAfter: 24
            }
          }
        }
        
        // Check if we have extra options on the HTML
        if (this.swiper.dataset.options) {
          const options = JSON.parse(this.swiper.dataset.options);
          if (options) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...options
            }
          }
        }

        // Call swiper with selected swiper element and options
        this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

        this.onSlideChange();
        this.displayInstagramFeed();
        this.handleDesignModeEvents();
      }


      /**
       * Fetch Instagram feed
       * @returns json (feed)
       */
      async fetchInstagramFeed() {
        if (!this.feedUrl) return;
        try {
          const response = await fetch(this.feedUrl);
          const json = await response.json();

          // Catch error status and throw error
          if (json.status === 'error') throw new Error(json.message);
          
          return json;

        } catch (error) {
          console.error(error);
          return;
        }
      }


      /**
       * Display the Instagram feed
       * @returns void
       */
      async displayInstagramFeed() {
        if (!this.feedUrl) return;
        const instagramPosts = await this.fetchInstagramFeed();
        
        // If feed is empty, return
        if (!instagramPosts) return;
        
        // Remove all slides
        this.swiperInstance.removeAllSlides();

        await instagramPosts.forEach((post, i) => {
          // Break if index is higher than number of posts
          if (i > this.numberOfPosts) return;

          // Add the slides
          this.swiperInstance.addSlide(i, 
            post.mediaType === 'IMAGE' ? this.buildImagePost(post)
            : post.mediaType === 'VIDEO' ? this.buildVideoPost(post)
            : this.buildCarouselAlbumPost(post)
          );
        });

        // Init Carousel Album slider
        const swiperCarouselAlbums = this.querySelectorAll('.swiper-carousel-album');

        if (swiperCarouselAlbums.length) {
          swiperCarouselAlbums.forEach(swiperCarouselAlbum => {
            this.initCarouselAlbumSwiper(swiperCarouselAlbum);
          });
        }


        // Handle video autoplay for visible slides
        this.swiperInstance.slides.forEach(slide => this.handleVideoAutoplay(slide));
      }


      /**
       * Build image node
       * @param {Object} post 
       * @returns image node
       */
      buildImagePost(post) {
        const { dimensions, mediaType, mediaUrl, permalink } = post;

        return `
          <div class="swiper-slide">
            <a href="${permalink}" target="_blank" rel="noopener noreferrer">
              <figure class="img img--wrapper img--cover instagram-feed__image">
                <img
                  src="${mediaUrl}"
                  alt="${mediaType} from the Instagram of @mystoov"
                  width="${dimensions.width}"
                  height="${dimensions.height}"
                  loading="lazy"
                  title="${mediaType} from the Instagram of @mystoov"
                  crop="center"
                  style="aspect-ratio:1/1;"
                >
              </figure>
            </a>
          </div>
        `
      }


      /**
       * Build video node
       * @param {Object} post 
       * @returns video node
       */
      buildVideoPost(post) {
        const { dimensions, mediaType, mediaUrl, permalink } = post;

        // Get media url type (.mp4 etc.)
        let mediaUrlType = mediaUrl.split('?')[0];
        const lastDotInUrl = mediaUrlType.lastIndexOf('.');
        mediaUrlType = mediaUrlType.slice(lastDotInUrl + 1);

        return `
          <div class="swiper-slide">
            <a href="${permalink}" target="_blank" rel="noopener noreferrer">
              <div class="video instagram-feed__video">
                <video 
                  width="${dimensions.width}"
                  height="${dimensions.height}"
                  title="${mediaType} from the Instagram of @mystoov"
                  muted
                  loop
                >
                  <source src="${mediaUrl}" type="video/${mediaUrlType}">
                  Your browser does not support the video tag.
                </video>
              </div>
            </a>
          </div>
        `
      }


      /**
       * Build carousel album node
       * @param {Object} post 
       * @returns nested slider node
       */
      buildCarouselAlbumPost(post) {
        const { children, dimensions, permalink } = post;

        return `
          <div class="swiper-slide">
            <a href="${permalink}" target="_blank" rel="noopener noreferrer">
              <div class="swiper swiper-carousel-album swiper-container">
                <div class="swiper-wrapper">
                  ${this.buildCarouselAlbumChildPosts(children, dimensions)}
                </div>
              </div>
            </a>
          </div>
        `
      }


      /**
       * Build video node
       * @param {Object} children 
       * @returns video node
       */
      buildCarouselAlbumChildPosts(posts) {
        let slides = []

        posts.forEach((post, dimensions) => {
          const { mediaType, mediaUrl } = post;

          const slide = `
            <div class="swiper-slide">
              <figure class="img img--wrapper img--cover instagram-feed__image">
                <img
                  src="${mediaUrl}"
                  alt="${mediaType} from the Instagram of @mystoov"
                  width="${dimensions.width}"
                  height="${dimensions.height}"
                  loading="lazy"
                  title="${mediaType} from the Instagram of @mystoov"
                  crop="center"
                  style="aspect-ratio:1/1;"
                >
              </figure>
            </div>
          `

          slides.push(slide)
        });

        return slides.join("")
      }


      initCarouselAlbumSwiper(albumSwiper) {
        this.swiperCarouselAlbumInstance = new Swiper(albumSwiper, {
          autoplay: {
            delay: 3000,
          },
          effect: 'fade',
          fadeEffect: {
            crossFade: true
          },
          allowTouchMove: false
        });
      }


      /**
       * Handle slide changes
       */
      onSlideChange() {
        if (!this.feedUrl) return;
        this.swiperInstance.on('slideChange', () => {
          this.swiperInstance.slides.forEach(slide => {
            this.handleVideoAutoplay(slide);
          });
        });
      }


      /**
       * Handle video autoplay
       * @param {Node} slide 
       * @returns void
       */
      handleVideoAutoplay(slide) {
        const video = slide.querySelector('video');
        if (!video) return;
        (slide.classList.contains('swiper-slide-visible')) ? video.play() : video.pause();
      }

            
      /**
      * Handles the theme editor block change/edit event
      * @param {Object} event
      */
      handleBlockSelect({dataset}) {
        // Check if the slide index is set
        if (!("swiperSlideIndex" in dataset)) return;
  
        // Set the slide index based on loop settings or not
        let swipeToSlideIndex = parseInt(dataset.swiperSlideIndex) - 1;
        if(this.swiperOptions.loop) swipeToSlideIndex = parseInt(dataset.swiperSlideIndex) + 1;
  
        // Slide to slide based on the data attribute from the target
        this.swiperInstance.slideTo(swipeToSlideIndex, 1000);
        
      }


      /**
       * Handle design mode events
       * @returns void
       */
      handleDesignModeEvents() {
        if (!Shopify.designMode) return;

        // Update the swiper when the section event is triggered
        ['shopify:section:load', 'shopify:block:select', 'shopify:block:deselect'].forEach((inputEvent) => {
          window.addEventListener(inputEvent, (event) => {
            this.swiperInstance.update();

            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }

            if (inputEvent === 'shopify:block:select' || inputEvent === 'shopify:block:deselect') this.handleBlockSelect(event.target);
          });
        });
      }
    }

    window.InstagramFeed = InstagramFeed;
    customElements.define('instagram-feed', InstagramFeed);
  }
  
})();